import { useTransactionCertificateStore } from "../../../../../store/transaction-certificates.store";

export interface BuyerSellerDetails {
  // Common Details
  tcId: string;
  // Seller Details
  sellerLicenseNumber: string;
  sellingOnBehalfOfFlag: string;
  sellerName: string;
  sellerTEId: string;
  sellerAddress1: string;
  sellerAddress2: string;
  sellerAddress3: string;
  sellerPostcode: string;
  sellerTown: string;
  sellerStateOrProvince: string;
  sellerCountryOrArea: string;
  sellerCertifiedOrganizationName: string;
  sellerSCNumber: string;
  tcSellerProductDeclarations: string;
  // Buyer Details
  buyerLicenseNumber: string;
  buyingOnBehalfOfFlag: string;
  buyerName: string;
  buyerTEId: string;
  buyerAddress1: string;
  buyerAddress2: string;
  buyerAddress3: string;
  buyerPostcode: string;
  buyerTown: string;
  buyerStateOrProvince: string;
  buyerCountryOrArea: string;
  buyerCertifiedOrganizationName: string;
  buyerScCbLicensingCode: string;
  buyerSCNumber: string;
  isBuyerTeCertified: string;
  // Non-Certified Trader
  nonCertifiedTrader: INonCertifiedTrader[];
}

export interface INonCertifiedTrader {
  sellerNonCertifiedTraderName: string;
  sellerNonCertifiedTraderTeId: string;
}

export const buyerSellerDetails: BuyerSellerDetails = {
  // Common Details
  tcId: "",
  // Seller Details
  isBuyerTeCertified: "Yes",
  sellerLicenseNumber: "",
  sellingOnBehalfOfFlag: "Yes",
  sellerName: "",
  sellerTEId: "",
  sellerAddress1: "",
  sellerAddress2: "",
  sellerAddress3: "",
  sellerPostcode: "",
  sellerTown: "",
  sellerStateOrProvince: "",
  sellerCountryOrArea: "",
  sellerCertifiedOrganizationName: "",
  sellerSCNumber: "",
  tcSellerProductDeclarations: "",
  // Buyer Details
  buyerLicenseNumber: "",
  buyingOnBehalfOfFlag: "Yes",
  buyerName: "",
  buyerTEId: "",
  buyerAddress1: "",
  buyerAddress2: "",
  buyerAddress3: "",
  buyerPostcode: "",
  buyerTown: "",
  buyerStateOrProvince: "",
  buyerCountryOrArea: "",
  buyerCertifiedOrganizationName: "",
  buyerScCbLicensingCode: "",
  buyerSCNumber: "",
  // Non-Certified Trader
  nonCertifiedTrader: [],
};

const normalizeNaValue = (value?: string | null) => {
  if (typeof value === "string" && value.trim().toLowerCase() === "na") {
    return "na";
  }
  return value || "";
};

export const getTcSellerInformation = (data: any) =>
  data?.tcSellerInformation || data?.sellerInformation || data?.sellerDetails || {};

export const getTcBuyerInformation = (data: any) =>
  data?.tcBuyerInformation || data?.buyerInformation || data?.buyerDetails || {};

export const getTcSellerNonCertifiedTraderInformation = (data: any) =>
  data?.tcSellerNonCertifiedTraderInformation ||
  data?.sellerNonCertifiedTraderInformation ||
  [];

const trimStr = (val: any): string => {
  if (typeof val !== "string") return val ?? "";
  return val.trim();
};

export const mapBuyerSellerDetailsApiResponseToInitialValues = (data: any) => {
  const sellerInformation = getTcSellerInformation(data);
  const buyerInformation = getTcBuyerInformation(data);

  return {
    tcId: trimStr(data.tcId),
    // Seller Details - Updated to match API response structure
    sellerLicenseNumber: trimStr(
      sellerInformation?.sellerCbClientNo ||
      sellerInformation?.sellerLicenseNo ||
      sellerInformation?.sellerLicenseNumber ||
      sellerInformation?.sellerCbClientNumber ||
      sellerInformation?.certifiedOrganizationCbClientNo ||
      data?.sellerLicenseNumber ||
      data?.sellerCbClientNo ||
      data?.sellerLicenseNo
    ),
    sellingOnBehalfOfFlag: trimStr(sellerInformation?.sellingOnBehalfOfFlag) || "Yes",
    sellerName: trimStr(sellerInformation?.sellerName),
    sellerTEId: trimStr(sellerInformation?.sellerTeId),
    sellerAddress1: trimStr(sellerInformation?.sellerStreetAddress || sellerInformation?.sellerAddress1),
    sellerAddress2: trimStr(sellerInformation?.sellerAddress2),
    sellerAddress3: trimStr(sellerInformation?.sellerAddress3),
    sellerPostcode: trimStr(sellerInformation?.sellerPostcode),
    sellerTown: trimStr(sellerInformation?.sellerTown),
    sellerStateOrProvince: trimStr(sellerInformation?.sellerStateOrProvince),
    sellerCountryOrArea: trimStr(sellerInformation?.sellerCountryOrArea),
    sellerCertifiedOrganizationName: trimStr(sellerInformation?.sellerCertifiedOrganizationName),
    sellerSCNumber: trimStr(sellerInformation?.sellerScNo),
    tcSellerProductDeclarations: trimStr(sellerInformation?.tcSellerProductDeclarations),
    // Buyer Details - Updated to match API response structure
    buyerLicenseNumber: trimStr(
      buyerInformation?.buyerCbClientNo ||
      buyerInformation?.buyerLicenseNo ||
      buyerInformation?.buyerLicenseNumber ||
      buyerInformation?.buyerCbClientNumber ||
      buyerInformation?.certifiedOrganizationCbClientNo ||
      data?.buyerLicenseNumber ||
      data?.buyerCbClientNo ||
      data?.buyerLicenseNo
    ),
    buyingOnBehalfOfFlag: trimStr(buyerInformation?.buyingOnBehalfOfFlag) || "Yes",
    buyerName: trimStr(buyerInformation?.buyerName),
    buyerTEId: trimStr(buyerInformation?.buyerTeId),
    buyerAddress1: trimStr(buyerInformation?.buyerStreetAddress || buyerInformation?.buyerAddress1),
    buyerAddress2: trimStr(buyerInformation?.buyerAddress2),
    buyerAddress3: trimStr(buyerInformation?.buyerAddress3),
    buyerPostcode: trimStr(buyerInformation?.buyerPostcode),
    buyerTown: trimStr(buyerInformation?.buyerTown),
    buyerStateOrProvince: trimStr(buyerInformation?.buyerStateOrProvince),
    buyerCountryOrArea: trimStr(buyerInformation?.buyerCountryOrArea),
    buyerCertifiedOrganizationName: trimStr(buyerInformation?.buyerCertifiedOrganizationName),
    buyerScCbLicensingCode: trimStr(buyerInformation?.buyerScCbLicensingCode),
    buyerSCNumber: trimStr(buyerInformation?.buyerScNo),
    isBuyerTeCertified: trimStr(buyerInformation?.isBuyerTeCertified) || "Yes",
    // Non-Certified Trader
    nonCertifiedTrader: (getTcSellerNonCertifiedTraderInformation(data) || []).map((trader: any) => ({
      sellerNonCertifiedTraderName: trimStr(trader.sellerNonCertifiedTraderName),
      sellerNonCertifiedTraderTeId: trimStr(trader.sellerNonCertifiedTraderTeId),
    }))
  };
};


export const mapReturnTcAutofillFromSource = (
  sourceData: any,
  returnTcId: string
): BuyerSellerDetails => {
  const sourceSeller = getTcSellerInformation(sourceData);
  const sourceBuyer = getTcBuyerInformation(sourceData);

  const normalizeFlag = (flag?: string) => {
    if (!flag) return "Yes";
    const cleaned = flag.trim().toUpperCase();
    if (cleaned === "Y" || cleaned === "YES") return "Yes";
    if (cleaned === "N" || cleaned === "NO") return "No";
    return "Yes";
  };

  const getFirstScNo = (scVal: any): string => {
    if (Array.isArray(scVal)) return trimStr(scVal[0]);
    return trimStr(scVal);
  };

  const blankIfNa = (value: string): string =>
    ["na", "n/a"].includes(value.trim().toLowerCase()) ? "" : value;

  return {
    ...buyerSellerDetails,
    tcId: returnTcId || "",
    // Return Seller  <-  Source Buyer
    sellerLicenseNumber: blankIfNa(trimStr(sourceBuyer?.buyerCbClientNo || sourceBuyer?.buyerLicenseNo)),
    sellingOnBehalfOfFlag: normalizeFlag(sourceBuyer?.buyingOnBehalfOfFlag || sourceBuyer?.buyingOnBehalfOfFlag),
    sellerName: trimStr(sourceBuyer?.buyerName),
    sellerTEId: trimStr(sourceBuyer?.buyerTeId),
    sellerAddress1: trimStr(sourceBuyer?.buyerStreetAddress || sourceBuyer?.buyerAddress1),
    sellerAddress2: trimStr(sourceBuyer?.buyerAddress2),
    sellerAddress3: trimStr(sourceBuyer?.buyerAddress3),
    sellerPostcode: trimStr(sourceBuyer?.buyerPostcode),
    sellerTown: trimStr(sourceBuyer?.buyerTown),
    sellerStateOrProvince: trimStr(sourceBuyer?.buyerStateOrProvince),
    sellerCountryOrArea: trimStr(sourceBuyer?.buyerCountryOrArea),
    sellerCertifiedOrganizationName: blankIfNa(trimStr(sourceBuyer?.buyerCertifiedOrganizationName)),
    sellerSCNumber: blankIfNa(getFirstScNo(sourceBuyer?.buyerScNo)),
    tcSellerProductDeclarations: "",
    // Return Buyer  <-  Source Seller
    buyerLicenseNumber: trimStr(sourceSeller?.sellerCbClientNo || sourceSeller?.sellerLicenseNo),
    buyingOnBehalfOfFlag: normalizeFlag(sourceSeller?.sellingOnBehalfOfFlag),
    buyerName: trimStr(sourceSeller?.sellerName),
    buyerTEId: trimStr(sourceSeller?.sellerTeId),
    buyerAddress1: trimStr(sourceSeller?.sellerStreetAddress || sourceSeller?.sellerAddress1),
    buyerAddress2: trimStr(sourceSeller?.sellerAddress2),
    buyerAddress3: trimStr(sourceSeller?.sellerAddress3),
    buyerPostcode: trimStr(sourceSeller?.sellerPostcode),
    buyerTown: trimStr(sourceSeller?.sellerTown),
    buyerStateOrProvince: trimStr(sourceSeller?.sellerStateOrProvince),
    buyerCountryOrArea: trimStr(sourceSeller?.sellerCountryOrArea),
    buyerCertifiedOrganizationName: trimStr(sourceSeller?.sellerCertifiedOrganizationName),
    buyerScCbLicensingCode: trimStr(sourceSeller?.sellerCbLicensingCode || sourceData?.tcCbLicensingCode),
    buyerSCNumber: getFirstScNo(sourceSeller?.sellerScNo),
    isBuyerTeCertified: "Yes",
    // Non-certified trader is seller-role specific on the return TC; default to the
    // "na" placeholder so the (min 1) requirement is satisfied for review.
    nonCertifiedTrader: [
      { sellerNonCertifiedTraderName: "na", sellerNonCertifiedTraderTeId: "" },
    ],
  };
};

export const mapPayload = (jsonData: any, tcId: any, buyerStates?: any, sellerStates?: any,sellerScStandards?: string[], isReturnTc?: boolean) => {
  const buyerIsCertified = jsonData.isBuyerTeCertified !== "No";
  return {
    tcId: tcId,
    // Sent so the backend (container app) scopes the relaxed certified-seller rule to
    // Return TCs only — the return seller mirrors the original buyer and may be non-certified.
    tcIsReturn: isReturnTc ? "Yes" : "No",
    tcSellerInformation: {
      sellerCbClientNo: trimStr(jsonData.sellerLicenseNumber),
      sellingOnBehalfOfFlag: trimStr(jsonData.sellingOnBehalfOfFlag) || "Yes", // Default value as per the payload
      sellerName: trimStr(jsonData.sellerName),
      sellerTeId: trimStr(jsonData.sellerTEId),
      sellerStreetAddress: trimStr(jsonData.sellerAddress1),
      sellerAddress2: trimStr(jsonData.sellerAddress2),
      sellerAddress3: trimStr(jsonData.sellerAddress3),
      sellerPostcode: normalizeNaValue(trimStr(jsonData.sellerPostcode)),
      sellerTown: normalizeNaValue(trimStr(jsonData.sellerTown)),
      sellerStateOrProvince: trimStr(jsonData.sellerStateOrProvince),
      sellerCountryOrArea: trimStr(jsonData.sellerCountryOrArea),
      sellerCertifiedOrganizationName: trimStr(jsonData.sellerCertifiedOrganizationName),
      sellerScNo: trimStr(jsonData.sellerSCNumber),
      sellerScStandard: sellerScStandards || [],
      tcSellerProductDeclarations: trimStr(jsonData.tcSellerProductDeclarations),
      sellerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.sellerCountryOrArea ?? ""))?.label ?? "",  
      sellerStateOrProvinceDescription: sellerStates.find((option: any) => option.value === (jsonData.sellerStateOrProvince ?? ""))?.label ?? ""
    },
    tcBuyerInformation: {
      isBuyerTeCertified: trimStr(jsonData.isBuyerTeCertified) || "Yes", // Default value as per the payload
      buyerCbClientNo: trimStr(jsonData.buyerLicenseNumber), // Default value as per the payload
      buyingOnBehalfOfFlag: trimStr(jsonData.buyingOnBehalfOfFlag) || "Yes", // Default value as per the payload
      buyerName: trimStr(jsonData.buyerName),
      buyerTeId: trimStr(jsonData.buyerTEId),
      buyerStreetAddress: trimStr(jsonData.buyerAddress1),
      buyerAddress2: trimStr(jsonData.buyerAddress2), // Not available in the source JSON
      buyerAddress3: trimStr(jsonData.buyerAddress3), // Not available in the source JSON
      buyerPostcode: normalizeNaValue(trimStr(jsonData.buyerPostcode)),
      buyerTown: normalizeNaValue(trimStr(jsonData.buyerTown)),
      buyerStateOrProvince: trimStr(jsonData.buyerStateOrProvince),
      buyerCountryOrArea: trimStr(jsonData.buyerCountryOrArea),
      buyerCertifiedOrganizationName: buyerIsCertified ? normalizeNaValue(trimStr(jsonData.buyerCertifiedOrganizationName)) : "na",
      buyerScCbLicensingCode: buyerIsCertified ? trimStr(jsonData.buyerScCbLicensingCode) : "",
      buyerScNo: buyerIsCertified ? normalizeNaValue(trimStr(jsonData.buyerSCNumber)) : "na",
      buyerCountryOrAreaDescription: useTransactionCertificateStore.getState().getCountryCodes.find((option: any) => option.value === (jsonData.buyerCountryOrArea ?? ""))?.label ?? "",
      buyerStateOrProvinceDescription: buyerStates.find((option: any) => option.value === (jsonData.buyerStateOrProvince ?? ""))?.label ?? ""
    },
    tcSellerNonCertifiedTraderInformation: (jsonData.nonCertifiedTrader || []).map((trader: INonCertifiedTrader) => ({
      sellerNonCertifiedTraderName: normalizeNaValue(trimStr(trader.sellerNonCertifiedTraderName)),
      sellerNonCertifiedTraderTeId: trimStr(trader.sellerNonCertifiedTraderTeId),
    })),
  };
};
